import type { CalendarEvent } from "./CalendarEventTypes";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";
import { getStatusColor, getStatusSoftStyle } from "@/lib/utils/clinic-appointment-status";

const EVENT_TYPE_CLASSES: Record<string, string> = {
  consulta:
    "bg-sky-50 text-sky-950",
  cirurgia:
    "bg-rose-50 text-rose-950",
  banho_tosa:
    "bg-emerald-50 text-emerald-950",
  retorno:
    "bg-amber-50 text-amber-950",
  vacina:
    "bg-indigo-50 text-indigo-950",
  aplicacao:
    "bg-indigo-50 text-indigo-950",
  outro:
    "bg-violet-50 text-violet-950",
  // behavior keys
  padrao:
    "bg-sky-50 text-sky-950",
};

const EVENT_TYPE_ICON_CLASSES: Record<string, string> = {
  consulta: "bg-sky-100 text-sky-700",
  cirurgia: "bg-rose-100 text-rose-700",
  banho_tosa: "bg-emerald-100 text-emerald-700",
  retorno: "bg-amber-100 text-amber-700",
  vacina: "bg-indigo-100 text-indigo-700",
  aplicacao: "bg-indigo-100 text-indigo-700",
  outro: "bg-violet-100 text-violet-700",
  // behavior keys
  padrao: "bg-sky-100 text-sky-700",
};

const EVENT_TYPE_BORDER_COLORS: Record<string, string> = {
  consulta: "#0ea5e9",
  cirurgia: "#f43f5e",
  banho_tosa: "#10b981",
  retorno: "#f59e0b",
  vacina: "#6366f1",
  aplicacao: "#6366f1",
  outro: "#8b5cf6",
  padrao: "#0ea5e9",
};

export const getEventTypeBorderColor = (type: string): string =>
  EVENT_TYPE_BORDER_COLORS[type] ?? EVENT_TYPE_BORDER_COLORS.outro;

const EVENT_TYPE_BORDER_CLASSES: Record<string, string> = {
  consulta:
    "!border-l-sky-500",
  cirurgia:
    "!border-l-rose-500",
  banho_tosa:
    "!border-l-emerald-500",
  retorno:
    "!border-l-amber-500",
  vacina:
    "!border-l-indigo-500",
  aplicacao:
    "!border-l-indigo-500",
  outro:
    "!border-l-violet-500",
  // behavior keys
  padrao:
    "!border-l-sky-500",
};

const STATUS_BORDER_CLASSES: Record<string, string> = {
  cancelado:
    "!border-l-red-600",
  faltou:
    "!border-l-slate-600",
  pago:
    "!border-l-emerald-600",
  entregue:
    "!border-l-emerald-600",
  finalizado:
    "!border-l-teal-500",
  em_atendimento:
    "!border-l-amber-600",
  confirmado:
    "!border-l-blue-600",
  agendado:
    "!border-l-slate-400",
};

export const getStatusBorderClasses = (status: string): string =>
  STATUS_BORDER_CLASSES[status] ?? STATUS_BORDER_CLASSES.agendado;

/** Cor de fundo (hex) por tipo, para uso em variável CSS quando o evento está selecionado */
const EVENT_TYPE_BG_HEX: Record<string, string> = {
  consulta: "#f0f9ff",
  cirurgia: "#fff1f2",
  banho_tosa: "#ecfdf5",
  retorno: "#fffbeb",
  vacina: "#eef2ff",
  aplicacao: "#eef2ff",
  outro: "#f5f3ff",
  // behavior keys
  padrao: "#f0f9ff",
};

export const getEventTypeBackgroundColor = (type: string): string =>
  EVENT_TYPE_BG_HEX[type] ?? EVENT_TYPE_BG_HEX.outro;

/** Converts a hex color to rgba with the given opacity (0–1). Falls back to undefined if invalid. */
export const hexToRgba = (hex: string, opacity: number): string | undefined => {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  if (full.length !== 6) return undefined;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getEventTypeClasses = (type: string): string =>
  EVENT_TYPE_CLASSES[type] ?? EVENT_TYPE_CLASSES.outro;

export const getEventTypeIconClasses = (type: string): string =>
  EVENT_TYPE_ICON_CLASSES[type] ?? EVENT_TYPE_ICON_CLASSES.outro;

export const getEventTypeBorderClasses = (type: string): string =>
  EVENT_TYPE_BORDER_CLASSES[type] ?? EVENT_TYPE_BORDER_CLASSES.outro;

export const getEventPetsLabel = (event: CalendarEvent): string => {
  const pets = Array.isArray(event.pets) ? event.pets : [];
  const names = pets
    .map((p) => p?.name?.trim?.() ?? "")
    .filter(Boolean);
  if (names.length > 0) return names.join(", ");
  const fallback = event.pet?.name?.trim?.();
  return fallback ? fallback : "Agendamento";
};

export const getStatusColorValue = (status: string, statuses?: AppointmentStatus[]) =>
  getStatusColor(status, statuses);

export const getStatusSoftColorStyle = (status: string, statuses?: AppointmentStatus[]) =>
  getStatusSoftStyle(status, statuses);
